import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Avatar,
  InputGroup,
  InputLeftElement,
  Input,
  Checkbox,
} from '@chakra-ui/react';
import { useQuery, QueryFunctionContext } from '@tanstack/react-query';
import { MagnifyingGlass, User } from 'phosphor-react';
import { useUsers } from '../../hooks/useUsers';
import { IKanbanUserOption, KanbanPapel, useKanban } from '../../hooks/useKanban';

interface UserPapelPickerProps {
  // Rótulo do bloco (ex.: "Responsaveis", "Supervisores").
  label: string;
  // Papel deste bloco — só p/ diferenciar a cor/acento e a chave de query.
  papel: KanbanPapel;
  // Usuários já selecionados neste papel.
  selected: IKanbanUserOption[];
  // Alterna (adiciona/remove) um usuário neste papel.
  // eslint-disable-next-line no-unused-vars
  onToggle: (user: IKanbanUserOption) => void;
  // Se true, mostra "(obrigatorio)" no header (responsável).
  obrigatorio?: boolean;
  // Filtra a busca por departamentos (mesmo comportamento do modal).
  departamentoIds?: number[];
}

// Bloco reutilizável de seleção de usuários por papel (busca + chips + lista).
// Extraído p/ não duplicar ~80 linhas entre Responsáveis × Supervisores nos
// modais de card e no Estatuto (Task #496).
export const UserPapelPicker: React.FC<UserPapelPickerProps> = ({
  label,
  papel,
  selected,
  onToggle,
  obrigatorio = false,
  departamentoIds,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const { getUsers } = useUsers();
  const { getUsersByDepartamentos } = useKanban();

  // Acento de cor por papel: responsável azul, supervisor roxo.
  const accent = papel === 'supervisor' ? 'purple' : 'blue';

  // Debounce da busca (300ms), espelhando o padrão do KanbanAddCardModal.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const temDepto = !!departamentoIds && departamentoIds.length > 0;

  const { data: users = [] } = useQuery<IKanbanUserOption[]>({
    queryKey: ['user-papel-picker', papel, search, departamentoIds],
    queryFn: async (): Promise<IKanbanUserOption[]> => {
      if (temDepto) {
        // Endpoint por departamentos não aceita busca → filtra por nome no cliente.
        const res = await getUsersByDepartamentos(departamentoIds as number[]);
        const termo = search.trim().toLowerCase();
        return termo
          ? res.data.filter(u => (u.nome ?? '').toLowerCase().includes(termo))
          : res.data;
      }
      const ctx = {
        queryKey: ['users', { page: 1, size: 50, search }],
      } as unknown as QueryFunctionContext;
      const res = await getUsers(ctx);
      return res.data.items.map(u => ({
        id: u.id as number,
        nome: u.nome,
        email: u.email,
        imagem_url: u.imagem_url,
      }));
    },
  });
  const isSelected = (userId?: number) =>
    userId !== undefined && selected.some(u => u.id === userId);

  return (
    <Box>
      <Flex align="center" mb={2} gap={1}>
        <Text
          fontSize="12px"
          fontWeight="600"
          color="gray.500"
          textTransform="uppercase"
          letterSpacing="0.05em"
        >
          {label}
        </Text>
        {obrigatorio && (
          <Text fontSize="11px" color="orange.400" ml={1}>
            (obrigatorio)
          </Text>
        )}
        {selected.length > 0 && (
          <Text fontSize="11px" color={`${accent}.500`} fontWeight="600" ml={1}>
            {selected.length}
          </Text>
        )}
      </Flex>

      {/* Chips dos selecionados */}
      {selected.length > 0 && (
        <Flex wrap="wrap" gap={1.5} mb={2}>
          {selected.map(user => (
            <Flex
              key={user.id}
              align="center"
              bg={`${accent}.50`}
              px={2}
              py={0.5}
              borderRadius="full"
              border="1px solid"
              borderColor={`${accent}.100`}
              cursor="pointer"
              onClick={() => onToggle(user)}
              _hover={{ bg: `${accent}.100` }}
              transition="background 0.1s"
            >
              <Avatar size="2xs" name={user.nome} mr={1.5} />
              <Text fontSize="11px" fontWeight="500" color={`${accent}.700`}>
                {user.nome}
              </Text>
            </Flex>
          ))}
        </Flex>
      )}

      {/* Busca */}
      <InputGroup size="sm" mb={2}>
        <InputLeftElement>
          <MagnifyingGlass size={14} color="gray" />
        </InputLeftElement>
        <Input
          placeholder="Buscar..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          borderRadius="lg"
          fontSize="13px"
          _focus={{
            borderColor: `${accent}.400`,
            boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.1)',
          }}
        />
      </InputGroup>

      {/* Lista */}
      <Box
        maxH="140px"
        overflowY="auto"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="xl"
        css={{
          '&::-webkit-scrollbar': { width: '3px' },
          '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '2px' },
        }}
      >
        {users.length === 0 ? (
          <Flex align="center" justify="center" py={4} color="gray.300">
            <User size={16} />
            <Text ml={2} fontSize="12px">
              Nenhum usuario
            </Text>
          </Flex>
        ) : (
          <Flex direction="column" p={1}>
            {users.map(user => {
              const sel = isSelected(user.id);
              return (
                <Flex
                  key={user.id}
                  align="center"
                  px={2.5}
                  py={1.5}
                  cursor="pointer"
                  bg={sel ? `${accent}.50` : 'transparent'}
                  borderRadius="lg"
                  _hover={{ bg: sel ? `${accent}.50` : 'gray.50' }}
                  onClick={() => onToggle(user)}
                  transition="background 0.1s"
                >
                  <Checkbox
                    isChecked={sel}
                    onChange={() => onToggle(user)}
                    mr={2}
                    size="sm"
                    colorScheme={accent}
                  />
                  <Avatar size="xs" name={user.nome} mr={2} />
                  <Box flex={1} minW={0}>
                    <Text fontSize="12px" fontWeight="500" noOfLines={1}>
                      {user.nome}
                    </Text>
                    <Text fontSize="10px" color="gray.400" noOfLines={1}>
                      {user.email}
                    </Text>
                  </Box>
                </Flex>
              );
            })}
          </Flex>
        )}
      </Box>
    </Box>
  );
};
